import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { TWO_FACTOR_CODE_LENGTH } from 'constants/General'

interface DisburseFormData {
  destinationWalletId: string
  confirmationOfCustomerDistribution: boolean
  confirmationOfIrreversible: boolean
  authenticationCode: string
}

// Validation schema
const disburseValidationSchema = yup.object().shape({
  destinationWalletId: yup.string().required('Wallet selection is required'),
  confirmationOfCustomerDistribution: yup
    .boolean()
    .oneOf([true], 'Confirmation is required')
    .required('Confirmation is required'),
  confirmationOfIrreversible: yup
    .boolean()
    .oneOf([true], 'Confirmation is required')
    .required('Confirmation is required'),
  authenticationCode: yup
    .string()
    .length(TWO_FACTOR_CODE_LENGTH, 'Authentication code must be 6 digits')
    .required('Authentication code is required'),
})

const DEFAULT_VALUES: DisburseFormData = {
  destinationWalletId: '',
  confirmationOfCustomerDistribution: false,
  confirmationOfIrreversible: false,
  authenticationCode: '',
}

interface UseRewardsDisburseFormProps {
  open: boolean
  onDisburse: (destinationWalletId: string, authenticationCode: string) => void
}

export const useRewardsDisburseForm = ({ open, onDisburse }: UseRewardsDisburseFormProps) => {
  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<DisburseFormData>({
    resolver: yupResolver(disburseValidationSchema),
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  })

  const destinationWalletId = watch('destinationWalletId')
  const confirmationOfCustomerDistribution = watch('confirmationOfCustomerDistribution')
  const confirmationOfIrreversible = watch('confirmationOfIrreversible')

  const handleDestinationWalletChange = (value: string) => {
    setValue('destinationWalletId', value, { shouldValidate: true })
  }

  const handleAuthenticationCodeChange = (value: string) => {
    setValue('authenticationCode', value, { shouldValidate: true })
  }

  const handleConfirmationOfCustomerDistributionChange = (checked: boolean | string) => {
    setValue('confirmationOfCustomerDistribution', Boolean(checked), {
      shouldValidate: true,
    })
  }

  const handleConfirmationOfIrreversibleChange = (checked: boolean | string) => {
    setValue('confirmationOfIrreversible', Boolean(checked), {
      shouldValidate: true,
    })
  }

  const onSubmit = (data: DisburseFormData) => {
    onDisburse(data.destinationWalletId, data.authenticationCode)
  }

  const handleFormSubmit = async () => {
    // Trigger validation for all fields to show errors
    const isValid = await trigger()
    if (isValid) {
      handleSubmit(onSubmit)()
    }
  }

  useEffect(() => {
    if (open) {
      reset()
    }
  }, [open, reset])

  return {
    destinationWalletId,
    confirmationOfCustomerDistribution,
    confirmationOfIrreversible,
    handleDestinationWalletChange,
    handleAuthenticationCodeChange,
    handleConfirmationOfCustomerDistributionChange,
    handleConfirmationOfIrreversibleChange,
    handleSubmit: handleFormSubmit,
    errors,
  }
}
